import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import techniciansJobApi from '../../../APIs/technicians';
import { ReactComponent as DeleteSVG } from '../../../assets/icons/delete.svg';
import { ReactComponent as UpdateSVG } from '../../../assets/icons/refresh.svg';
import {
  AdditionalRequirement,
  CostAndShipping,
  JobInformation,
  PropertyInformation,
  RequirementInformation,
} from '../../../components/ClientCreatedJobDetails';
import ConfirmationModalTechnician from '../../../components/ConfirmationModal/ConfirmationModal';
import {
  Button,
  Divider,
  Flex,
  Loader,
  Tag,
  Typography,
} from '../../../designComponents';
import { CREATE_JOBS_ROUTE } from '../../../routes/routePaths';
import { RootState } from '../../../store/selectors';
import { humanReadableDate, requestAPI } from '../../../utils';
import { ActionsContainer } from './style';

type Props = {};

const TechnicianJobDetails = (_props: Props) => {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [deleteJobStatus, setDeleteJobStatus] = useState({
    showModal: false,
    deleting: false,
  });

  const [loading, setLoading] = useState(false);
  const technicianJobsDetails = useSelector(
    (state: RootState) => state.technicianJobsDetails
  );
  const getTechnicianJob = async (id: string) => {
    try {
      setLoading(true);
      await requestAPI(techniciansJobApi.getTechnicianJobsDetail(id));
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (jobId) {
      getTechnicianJob(jobId);
    }
  }, [jobId]);

  const createdDate =
    typeof technicianJobsDetails.createdAt === 'string'
      ? humanReadableDate(technicianJobsDetails?.createdAt)
      : '';

  const additionalInfo =
    Array.isArray(technicianJobsDetails.additionalRequirement) &&
    technicianJobsDetails.additionalRequirement.length > 0
      ? technicianJobsDetails.additionalRequirement
      : [];
  const formatData = () => {
    if (typeof technicianJobsDetails !== 'undefined') {
      const newData = technicianJobsDetails?.logo?.map((item, index) => ({
        id: `logo-${index}`,
        URL: item,
      }));
      return newData;
    }
    return [];
  };

  const onUpdate = () => {
    navigate(`/create-jobs/technician?edit=job-${jobId}`);
  };

  const handleCloseDeleteModal = () => {
    setDeleteJobStatus({ deleting: false, showModal: false });
  };

  const onDelete = () => {
    setDeleteJobStatus((prev) => ({ ...prev, showModal: true }));
  };
  const handleDelete = async () => {
    try {
      if (!technicianJobsDetails?._id) {
        return;
      }

      setDeleteJobStatus((prev) => ({ ...prev, deleting: true }));
      await requestAPI(
        techniciansJobApi.deleteTechnician(technicianJobsDetails?._id)
      );
      navigate(CREATE_JOBS_ROUTE);
    } catch (error) {
      console.error('Error While Deleting Job:', error);
    } finally {
      handleCloseDeleteModal();
    }
  };

  return (
    <div className="contents">
      <div className="heading">
        <Typography variant="title">
          {technicianJobsDetails?.jobTitle}
        </Typography>
        <Tag type="success" style={{ padding: '3px 12px' }}>
          {technicianJobsDetails?.status}
        </Tag>
      </div>
      <Divider spaceY="20px" />
      {loading ? (
        <Loader
          type="spinner"
          style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
        />
      ) : (
        <>
          <JobInformation
            jobCreatedAt={createdDate || ''}
            jobTitle={technicianJobsDetails?.jobTitle || ''}
            notes={technicianJobsDetails?.notes || ''}
          />
          <Divider spaceY="20px" />
          <PropertyInformation
            addressOfProperty={
              technicianJobsDetails?.building?.buildingAddress || ''
            }
            buildingName={technicianJobsDetails?.building?.buildingName || ''}
            buildingId={technicianJobsDetails?.building?._id || ''}
          />
          <Divider spaceY="20px" />
          <RequirementInformation
            frameColor={technicianJobsDetails?.frameColor || ''}
            materialTypeOfDrawing={
              technicianJobsDetails?.drawingMaterialType || ''
            }
            resolutionOfLogo={technicianJobsDetails?.logoResolution || ''}
            roundedEdge={technicianJobsDetails?.roundedEdge ? 'Yes' : 'No'}
            sizeOfDrawing={technicianJobsDetails?.drawingSize || ''}
            uploadedLogos={formatData()}
          />
          <Divider spaceY="20px" />
          <CostAndShipping
            pricing={`${technicianJobsDetails?.pricing} ` || ''}
            shippingAddress={technicianJobsDetails?.shippingAddress || ''}
          />
          <Divider spaceY="20px" />
          {additionalInfo.length > 0 && (
            <>
              <AdditionalRequirement additionalInfo={additionalInfo} />
              <Divider spaceY="20px" />
            </>
          )}
          <Typography
            variant="normal"
            strong
            fontColor="secondary"
            className="section-title"
          >
            ACTIONS
          </Typography>
          <ActionsContainer>
            <Button variant="secondary" onClick={onUpdate}>
              <UpdateSVG />
              Update
            </Button>
            {technicianJobsDetails.status === 'ACTIVE' && (
              <Button variant="danger" onClick={onDelete}>
                <DeleteSVG /> Delete
              </Button>
            )}
          </ActionsContainer>
        </>
      )}
      <ConfirmationModalTechnician
        message={`Are you sure you want to delete "${technicianJobsDetails.jobTitle}" job?`}
        isOpen={deleteJobStatus.showModal}
        onClose={handleCloseDeleteModal}
        loading={deleteJobStatus.deleting}
        title="Delete Technician Job"
      >
        <Flex justifyContent="space-between">
          <Button
            variant="danger"
            onClick={handleDelete}
            isLoading={deleteJobStatus.deleting}
            isDisabled={deleteJobStatus.deleting}
          >
            Delete
          </Button>
          <Button
            variant="cancel"
            onClick={handleCloseDeleteModal}
            isDisabled={deleteJobStatus.deleting}
          >
            Cancel
          </Button>
        </Flex>
      </ConfirmationModalTechnician>
    </div>
  );
};

export default TechnicianJobDetails;
